// Zona para soltar un archivo, o elegirlo del equipo.
//
// No había ninguna en el repo: el único `input type="file"` es el de imágenes de
// la ficha técnica, y va oculto tras un botón. Ésta nace aparte porque arrastrar
// tiene trampas propias que no se ven hasta que fallan:
//
//   · `dragleave` salta también al pasar por encima de un hijo, así que un
//     contador de entradas y salidas es lo único que evita el parpadeo;
//   · sin `preventDefault` en `dragover`, el navegador ABRE el archivo y se
//     lleva por delante la aplicación entera con el trabajo sin guardar;
//   · y el teclado tiene que poder hacer lo mismo que el ratón, porque arrastrar
//     no es una opción para todo el mundo.
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  dark: boolean
  /** Extensiones admitidas, con punto: ['.xlsx', '.xls', '.pdf']. */
  extensiones: string[]
  /** Tope en bytes. Se comprueba aquí para no subir lo que se va a rechazar. */
  maxBytes: number
  /** Cuántos caben todavía. A 0 la zona se apaga: dejar soltar un sexto archivo
   *  para rechazarlo después es hacerle perder el tiempo al usuario. */
  huecos?: number
  onArchivos: (archivos: File[]) => void
  /** Código de error, que traduce quien llama: la pantalla es bilingüe. */
  onError: (codigo: string, valores?: Record<string, string | number>) => void
}

// NO tiene interruptor para apagarse, y es deliberado: lo tuvo, y apagada se
// comía el archivo sin decir nada —ni lo cogía ni explicaba por qué—. Si
// todavía falta algo para poder procesar lo que se suelte, eso se dice en el
// campo que falta y en el botón que no se puede pulsar, no quitándole al
// usuario el gesto de traer el archivo.
export function ZonaArrastre({
  dark, extensiones, maxBytes, huecos, onArchivos, onError,
}: Props) {
  const { t } = useTranslation()
  const input = useRef<HTMLInputElement>(null)
  const dentro = useRef(0)
  const [encima, setEncima] = useState(false)

  function admitir(lista: FileList | null) {
    const entrantes = Array.from(lista ?? [])
    if (entrantes.length === 0) return
    if (huecos != null && entrantes.length > huecos) {
      onError('demasiados_archivos', { max: huecos })
      return
    }
    // TODO O NADA, igual que el backend: si uno de los tres no vale, no entra
    // ninguno. Colar los buenos y callarse el malo dejaría un pedido incompleto
    // que el usuario cree completo.
    for (const archivo of entrantes) {
      const punto = archivo.name.lastIndexOf('.')
      const ext = punto >= 0 ? archivo.name.slice(punto).toLowerCase() : ''
      if (!extensiones.includes(ext)) {
        onError('tipo_no_admitido', { extension: ext || '—', archivo: archivo.name })
        return
      }
      if (archivo.size > maxBytes) {
        onError('archivo_demasiado_grande', {
          maxMb: Math.round(maxBytes / 1024 / 1024), archivo: archivo.name,
        })
        return
      }
      if (archivo.size === 0) {
        onError('archivo_vacio', { archivo: archivo.name })
        return
      }
    }
    onArchivos(entrantes)
  }

  const borde = encima
    ? 'border-sky-500 bg-sky-500/[0.07]'
    : dark
      ? 'border-white/15 hover:border-white/25'
      : 'border-slate-300 hover:border-slate-400'

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault()
        dentro.current += 1
        setEncima(true)
      }}
      onDragOver={(e) => {
        // Sin esto el navegador abre el archivo y se lleva la aplicación por
        // delante, con el trabajo sin guardar.
        e.preventDefault()
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        dentro.current -= 1
        if (dentro.current <= 0) {
          dentro.current = 0
          setEncima(false)
        }
      }}
      onDrop={(e) => {
        e.preventDefault()
        dentro.current = 0
        setEncima(false)
        // Varios a la vez: el cliente puede mandar el pedido repartido y el
        // flujo los junta en UN pedido.
        admitir(e.dataTransfer.files)
      }}
      className={`rounded-xl border-2 border-dashed transition-colors ${borde}`}
    >
      <input
        ref={input}
        type="file"
        hidden
        multiple
        accept={extensiones.join(',')}
        onChange={(e) => {
          admitir(e.target.files)
          // Para que elegir dos veces el MISMO archivo vuelva a disparar el
          // evento: sin esto, quien corrige el Excel y lo vuelve a soltar no ve
          // reaccionar la pantalla.
          e.target.value = ''
        }}
      />

      <button
        type="button"
        onClick={() => input.current?.click()}
        className="w-full flex flex-col items-center gap-3 px-6 py-10 cursor-pointer"
      >
        <span
          className={`w-11 h-11 rounded-xl grid place-items-center transition-colors ${
            encima
              ? 'bg-sky-500/15 text-sky-500'
              : dark ? 'bg-white/[0.06] text-slate-500' : 'bg-slate-100 text-slate-400'
          }`}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 16V4M12 4l-4 4M12 4l4 4" />
            <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
          </svg>
        </span>

        <span className="flex flex-col items-center gap-1">
          <span className={`text-[13px] font-medium ${dark ? 'text-slate-200' : 'text-slate-800'}`}>
            {encima
              ? t('salesOrders.desdeArchivo.soltar')
              : t('salesOrders.desdeArchivo.arrastra')}
          </span>
          <span className={`text-[11px] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
            {t('salesOrders.desdeArchivo.formatos', {
              formatos: extensiones.join(' · '),
              mb: Math.round(maxBytes / 1024 / 1024),
            })}
          </span>
        </span>

        <span
          className={`mt-1 px-3 py-1.5 rounded-lg text-[11px] font-medium ${
            dark ? 'bg-white/[0.07] text-slate-300' : 'bg-slate-100 text-slate-600'
          }`}
        >
          {t('salesOrders.desdeArchivo.buscar')}
        </span>
      </button>
    </div>
  )
}
